import { Fragment, PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SearchInput, combineClassnames, useOutsideClick } from 'app/common/presentation'
import { DropDownIcon } from 'app/common/presentation/icons'

import ActivityIndicator from 'components/ActivityIndicator/ActivityIndicator'
import { Button } from 'components/Button'
import { Checkbox } from 'components/Checkbox'

import styles from './DropDown.module.scss'

interface DropDownValue<T> {
  label: string
  value: T
}

const isDropDownValue = <T,>(value: unknown): value is DropDownValue<T> =>
  (value as DropDownValue<T>).label !== undefined && (value as DropDownValue<T>).value !== undefined

const isMultipleSelectionDropDown = <T,>(props: unknown): props is MultipleDropDownProps<T> =>
  (props as MultipleDropDownProps<T>).type !== undefined

export interface DropDownProps<T> extends PropsWithChildren {
  className?: string
  classNameMenu?: string
  data: (DropDownValue<T> | T)[]
  loading?: boolean
  onSelect: (selected: T) => void
  renderValueContent?: (value: T) => JSX.Element | string
  selected: T | null
  title?: string
}

export interface MultipleDropDownProps<T> extends Omit<DropDownProps<T>, 'selected' | 'type'> {
  onReset?: () => void
  selection: T[]
  type: 'multiple'
}

const renderValue = <T,>(
  value: DropDownValue<T> | T,
  renderValueContent?: (value: T) => JSX.Element | string,
) => {
  if (isDropDownValue(value)) {
    return <span>{value.label}</span>
  }

  if (renderValueContent) {
    const valueContent = renderValueContent(value)
    return typeof valueContent === 'string' ? <span>{valueContent}</span> : valueContent
  }

  if (typeof value === 'string') {
    return <span>{value}</span>
  }

  // should normally not happen => use 'N/A' as indicator if so
  return 'N/A'
}

const DropDown = <T,>({
  children,
  className,
  classNameMenu,
  data,
  loading,
  onSelect,
  renderValueContent,
  title,
  ...otherProps
}: DropDownProps<T> | MultipleDropDownProps<T>) => {
  const { t } = useTranslation('components')

  const [open, setOpen] = useState<undefined | 'down' | 'up'>()
  const [search, setSearch] = useState('')

  const ref = useOutsideClick<HTMLDivElement>(() => setOpen(undefined))

  const isMultipleSelection = isMultipleSelectionDropDown(otherProps)
  const selectedValues = isMultipleSelection
    ? (data.filter((entry) =>
        otherProps.selection.includes(isDropDownValue(entry) ? entry.value : entry),
      ) ?? [])
    : (data.filter(
        (entry) =>
          (otherProps as DropDownProps<T>).selected ===
          (isDropDownValue(entry) ? entry.value : entry),
      ) ?? [])

  const filteredData = useMemo(
    () =>
      data.filter((entry) =>
        JSON.stringify(entry).toLocaleLowerCase().includes(search.toLocaleLowerCase()),
      ),
    [data, search],
  )

  useEffect(() => {
    if (open && ref.current) {
      const dropdownRect = ref.current.getBoundingClientRect()
      const menu = ref.current.querySelector('div:has(> ul)')
      if (menu) {
        const menuHeight = menu.clientHeight
        const spaceAtTheBottom =
          document.documentElement.clientHeight -
          dropdownRect.top -
          dropdownRect.height -
          menuHeight
        const spaceAtTheTop = dropdownRect.top - menuHeight

        const upward = spaceAtTheBottom < 0 && spaceAtTheTop > spaceAtTheBottom
        setOpen(upward ? 'up' : 'down')
      }
    }
  }, [open, ref])

  const handleSelect = (value: T | DropDownValue<T>) => {
    onSelect(isDropDownValue(value) ? value.value : value)
    if (!isMultipleSelection) {
      setOpen(undefined)
    }
  }

  return (
    <div
      className={[styles.dropDown, data.length ? undefined : styles.disabled, className].join(' ')}
      ref={ref}
    >
      {children ? (
        <div onClick={() => setOpen(open ? undefined : 'down')}>{children}</div>
      ) : (
        <div className={styles.select} onClick={() => setOpen(open ? undefined : 'down')}>
          <div className={styles.selection}>
            {selectedValues.length
              ? selectedValues.map((selectedValue, index) => (
                  <Fragment key={index}>
                    <>
                      {renderValue(selectedValue, renderValueContent)}
                      {index < selectedValues.length - 1 && <span>,&nbsp;</span>}
                    </>
                  </Fragment>
                ))
              : (title ?? '-')}
          </div>
          {loading ? <ActivityIndicator size={'xxs'} /> : <DropDownIcon />}
        </div>
      )}

      {open && (
        <div
          className={combineClassnames(styles.menu, classNameMenu)}
          style={{
            left: children ? 'unset' : 0,
            ...(open === 'up' ? { bottom: 42 } : undefined),
          }}
        >
          {isMultipleSelection &&
            (children && title ? (
              <div className={styles.title}>{title}</div>
            ) : (
              <SearchInput
                autoFocus
                className={styles.search}
                onChange={setSearch}
                placeholder={t('dropdown.search')}
                value={search}
              />
            ))}
          <ul>
            {filteredData.map((value, index) => (
              <li key={index} onClick={() => handleSelect(value)}>
                {isMultipleSelection && <Checkbox checked={selectedValues.includes(value)} />}
                <div className={styles.value}>{renderValue(value, renderValueContent)}</div>
              </li>
            ))}
          </ul>
          {isMultipleSelection && otherProps.onReset && (
            <div className={styles.reset} onClick={otherProps.onReset}>
              <Button small variant="secondary">
                {t('dropdown.reset')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DropDown
